import { Alert, AlertTitle, Box, Chip, Stack, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import type { PackageOverlap, PackageOverlapReport } from "../http/foyer";

/**
 * "You are about to make the ninth copy of `basic-sweden`." (`ENT-3`,
 * `docs/content-model-v2.md` §8.)
 *
 * ## Why this component exists at all
 *
 * The portal held 244 packages over 93 distinct names — `basic-sweden` NINE
 * times — because there was nowhere to say *"same content, different customer,
 * different price"*, so an operator copied the package. `ENT-3` deleted the
 * importer so those rows are never recreated, but **deleting an importer does
 * not stop a human authoring the ninth copy by hand.** The server computes the
 * overlap; this is the half the operator can actually see, and without it the
 * guard is a field nobody reads.
 *
 * ## It warns, it never blocks
 *
 * §8 is explicit: *"They may proceed — having seen they are making the 9th copy
 * of `basic-sweden`."* A hard refusal would only ever fight the legitimate
 * case. So there is no confirmation step here and no disabled Save — the
 * operator is told, and decides.
 *
 * ## The wording is chosen from the numbers, not fixed
 *
 * Two shapes need opposite advice, and showing one percentage for both is how a
 * warning gets ignored:
 *
 *  - **Contained** (`onlyHere === 0`) — everything here is already granted
 *    elsewhere. The answer is to compose a Product from the existing package,
 *    or, when the difference is a few titles, to carve those out with excludes.
 *  - **Near-copy** (`onlyHere > 0`) — mostly the same, with a little of its own.
 *    The answer is additive slicing: `basic` and `basic + cnn`, never
 *    `basic − cnn`.
 */

/** The named titles, capped server-side. Nothing is invented here. */
function Sample({ ids }: { ids: string[] }) {
  if (ids.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
      {ids.map((id) => (
        <Chip key={id} label={id} size="small" variant="outlined" />
      ))}
    </Stack>
  );
}

function One({ o }: { o: PackageOverlap }) {
  const { t } = useTranslation("content");
  const contained = o.onlyHere === 0;
  // A carve-out worth naming: everything here is already there, and there is a
  // short, nameable list of what the other one has on top. That is the
  // "basic-sweden minus SVT 2" case, and naming the titles is the whole value.
  const carveOut = contained && o.onlyThere > 0;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: "fontWeightSemibold" }}>
        {contained
          ? t("packageOverlapNotice.containedIn", { name: o.name })
          : t("packageOverlapNotice.shareIn", {
              share: Math.round(o.share * 100),
              name: o.name,
            })}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {t("packageOverlapNotice.sharedStats", {
          shared: o.shared,
          count: o.shared + o.onlyHere,
          name: o.name,
          theirs: o.theirs,
        })}
        {o.onlyHere > 0
          ? t("packageOverlapNotice.onlyHere", { count: o.onlyHere })
          : ""}
        {o.onlyThere > 0
          ? t("packageOverlapNotice.onlyThere", { count: o.onlyThere })
          : ""}
      </Typography>

      {carveOut && o.onlyThere <= o.onlyThereSample.length ? (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="body2" color="text.secondary">
            {t("packageOverlapNotice.without", { name: o.name })}
          </Typography>
          <Sample ids={o.onlyThereSample} />
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            <Trans
              t={t}
              i18nKey="packageOverlapNotice.excludeAdvice"
              values={{ name: o.name }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Box>
      ) : contained ? (
        <Typography variant="body2" sx={{ mt: 0.75 }}>
          {t("packageOverlapNotice.nothingUnique", { name: o.name })}
        </Typography>
      ) : (
        <Box sx={{ mt: 0.75 }}>
          {o.onlyHereSample.length > 0 ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {t("packageOverlapNotice.onlyInThisPackage")}
              </Typography>
              <Sample ids={o.onlyHereSample} />
            </>
          ) : null}
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            <Trans
              t={t}
              i18nKey="packageOverlapNotice.sliceAdvice"
              values={{ name: o.name }}
              components={{ em: <em /> }}
            />
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function PackageOverlapNotice({
  report,
  onDismiss,
}: {
  report: PackageOverlapReport | null;
  onDismiss?: () => void;
}) {
  const { t } = useTranslation("content");
  if (!report || report.overlaps.length === 0) return null;

  return (
    <Alert
      severity="warning"
      onClose={onDismiss}
      sx={{ "& .MuiAlert-message": { width: "100%" } }}
    >
      <AlertTitle>
        {t("packageOverlapNotice.title", { count: report.overlaps.length })}
      </AlertTitle>

      <Typography variant="body2" color="text.secondary">
        {t("packageOverlapNotice.intro")}
      </Typography>

      {report.overlaps.map((o) => (
        <One key={o.id} o={o} />
      ))}

      {report.truncated || report.candidatesTruncated ? (
        <Typography
          variant="caption"
          color="text.secondary"
          component="p"
          sx={{ mt: 1.5 }}
        >
          {report.truncated ? t("packageOverlapNotice.truncated") : ""}
          {report.candidatesTruncated
            ? t("packageOverlapNotice.candidatesTruncated")
            : ""}
        </Typography>
      ) : null}
    </Alert>
  );
}
